import { DateTime } from 'luxon'

import { digObject, formatDate } from '@domain-group/javascript-utils'

import { CustomDeadlineModel } from '@models/types'

const DAYS = {
  0: 'Sun',
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
}

type IsCustomDeadlineTodayParams = {
  customDeadline: CustomDeadlineModel,
  userTimeZone: string,
}

export const isCustomDeadlineToday = (params: IsCustomDeadlineTodayParams) => {
  const { customDeadline, userTimeZone } = params
  const {
    dates, deadline_date, deadline_type, enabled_days, occurs_daily,
  } = customDeadline

  const todaysDateTimeObject = DateTime.now().setZone(userTimeZone)

  const lastAlertSent = digObject(dates, 'last_alert_sent.date_time', '')
  const luxonDayOfWeek = todaysDateTimeObject.weekday

  const parsedDeadlineDate = DateTime.fromISO(deadline_date, { zone: userTimeZone })

  const monthDifference = todaysDateTimeObject.diff(DateTime.fromISO(
    lastAlertSent,
    { zone: userTimeZone },
  ), 'months').months

  const isPast13Days = todaysDateTimeObject.diff(DateTime.fromISO(
    lastAlertSent,
    { zone: userTimeZone },
  ), 'days').days >= 13 || !lastAlertSent

  const isToday = (deadline_type === 'once' && parsedDeadlineDate.toISODate() === todaysDateTimeObject.toISODate())
    || (deadline_type === 'weekly' && occurs_daily)
    || (deadline_type === 'weekly' && enabled_days.includes(luxonDayOfWeek))
    || (deadline_type === 'fortnightly' && enabled_days.includes(luxonDayOfWeek) && isPast13Days)
    || (deadline_type === 'monthly'
      && enabled_days.includes(luxonDayOfWeek) && (monthDifference >= 1 || !lastAlertSent))

  return isToday
}

const renderDays = (enabledDays: Array<number>) => {
  if (enabledDays){
    // @ts-expect-error TS7053
    const dayString = enabledDays.reduce((string, day) => string.concat(`${DAYS[day]}, `), 'on ').slice(0, -2)
    return dayString
  }

  return ''
}

export const getDeadlineTypeDescription = (customDeadline: Partial<CustomDeadlineModel>) => {
  const {
    dates, deadline_type, enabled_days, occurs_daily,
  } = customDeadline

  // @ts-expect-error TS2345
  const deadlineDate = digObject(dates, 'deadline.date', '')
  const formattedDeadlineDate = deadlineDate ? formatDate(deadlineDate, 'dd/MM/yyyy', 'dd/MM/yy') : ''

  const hasEnabledDays = enabled_days && !!enabled_days.length
  const everyDaySelected = occurs_daily || (hasEnabledDays && enabled_days.length === 7)

  switch (deadline_type){
    case 'once':
      return formattedDeadlineDate ? `Once on ${formattedDeadlineDate}` : ''
    case 'weekly':
      if (everyDaySelected) return 'Daily'

      return hasEnabledDays ? `Weekly ${renderDays(enabled_days)}` : ''
    case 'fortnightly':
      return hasEnabledDays ? `Fortnightly ${renderDays(enabled_days)}` : ''
    case 'monthly':
      return hasEnabledDays ? `Monthly ${renderDays(enabled_days)}` : ''
    default:
      return ''
  }
}

export const mapToState = (customDeadline: Partial<CustomDeadlineModel>) => {
  const { deadline_date, deadline_time } = customDeadline

  const formattedDeadlineDate = deadline_date
    ? DateTime.fromISO(deadline_date).toFormat('yyyy-MM-dd')
    : ''
  const formattedDeadlineTime = deadline_time ? DateTime.fromISO(deadline_time).toFormat('HH:mm') : ''

  return {
    ...customDeadline,
    deadline_date: formattedDeadlineDate,
    deadline_time: formattedDeadlineTime,
  }
}

export default getDeadlineTypeDescription
